/**
 * SocketProvider - Global WebSocket Connection Manager
 * 
 * This component manages the WebSocket connection lifecycle:
 * - Connects when user is authenticated
 * - Disconnects on logout
 * - Provides socket context to child components
 * 
 * Mount this component once at the app root level.
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { socketManager } from '../lib/socketManager';
import { useClient } from '../contexts/ClientContext';
import { toast } from 'react-toastify';

// ============================================================================
// Types
// ============================================================================

interface SocketContextValue {
  /** Whether the socket is currently connected */
  isConnected: boolean;
  /** Emit an event to the server */
  emit: (event: string, payload?: any) => void;
  /** Subscribe to an event (returns unsubscribe function) */
  subscribe: (event: string, callback: (...args: any[]) => void) => () => void;
}

// ============================================================================
// Context
// ============================================================================

const SocketContext = createContext<SocketContextValue | null>(null);

// ============================================================================
// Provider Component
// ============================================================================

interface SocketProviderProps {
  children: React.ReactNode;
}

export function SocketProvider({ children }: SocketProviderProps) {
  const { client, loading: clientLoading } = useClient();
  const [isConnected, setIsConnected] = useState(false);
  // CRITICAL FIX: Track shown toast IDs to prevent infinite loops
  const shownToastIdsRef = useRef<Set<string>>(new Set());

  // Get auth token from localStorage
  const getToken = useCallback(() => {
    return localStorage.getItem('token') || localStorage.getItem('accessToken') || null;
  }, []);

  // Connect/disconnect based on auth state
  useEffect(() => {
    // Wait for client loading to complete
    if (clientLoading) {
      return;
    }

    const token = getToken();
    const userId = client?._id;
    
    // Get company from client object or decode from token
    let company = client?.company;
    if (!company && token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        company = payload.companyNumber || payload.company;
      } catch (e) {
        console.warn('[SocketProvider] Failed to decode company from token');
      }
    }

    // Check if we have valid auth
    if (!token || !userId || !company) {
      // No valid auth - disconnect if connected
      if (socketManager.isConnected()) {
        socketManager.disconnect();
      }
      setIsConnected(false);
      return;
    }

    // Skip connection for superadmin on certain company contexts
    // (superadmin doesn't always need real-time updates)
    if (company === 'superadmin' && client?.role !== 'superadmin') {
      return;
    }

    // Connect with auth
    socketManager.connect({
      token,
      userId: String(userId),
      company: String(company),
    });

    // Listen to connection state changes
    const unsubscribeConnection = socketManager.onConnectionChange((connected) => {
      setIsConnected(connected);
    });

    // Cleanup on unmount or when auth changes
    return () => {
      unsubscribeConnection();
    };
  }, [client?._id, client?.company, client?.role, clientLoading, getToken]);

  // Get queryClient for global cache invalidation
  const queryClient = useQueryClient();

  // Setup global event handlers (notifications, blocked user, etc.)
  useEffect(() => {
    if (!isConnected || !client?._id) return;

    // Global invalidation handler - invalidates React Query cache for all data types
    const unsubInvalidate = socketManager.subscribe('invalidate', (payload: any) => {
      const type = payload?.type;
      const company = payload?.company || client?.company;
      
      if (!type || !company) return;
      
      // Invalidate React Query cache based on event type
      if (type === 'contracts') {
        queryClient.invalidateQueries({ queryKey: ['contracts', company] });
      } else if (type === 'orders') {
        queryClient.invalidateQueries({ queryKey: ['orders', company] });
      } else if (type === 'slots' || type === 'appointments') {
        queryClient.invalidateQueries({ queryKey: ['appointments', company] });
      } else if (type === 'clients') {
        queryClient.invalidateQueries({ queryKey: ['clients', company] });
      } else if (type === 'tasks') {
        queryClient.invalidateQueries({ queryKey: ['tasks', company] });
      } else if (type === 'leads') {
        // PHASE 3: DO NOT invalidate here - useRealtimeInvalidation handles leads with NEWER-WINS
        // Invalidating here causes refetch that can overwrite fresh mutation results
        // queryClient.invalidateQueries({ queryKey: ['leads', company] });
      } else if (type === 'agents') {
        queryClient.invalidateQueries({ queryKey: ['agents', company] });
      } else if (type === 'inventory') {
        queryClient.invalidateQueries({ queryKey: ['inventory', company] });
      }
    });

    // Handle notification events (show toast)
    // CRITICAL FIX: Skip reminder notifications - they're handled by GlobalReminderPopup
    // Also add deduplication to prevent infinite loops
    const unsubNotification = socketManager.subscribe('notification', (payload: any) => {
      // CRITICAL: Don't show toast for reminder notifications - the popup will handle it
      // The reminder worker emits both 'notification' (for toast) and 'reminder-popup' (for popup)
      // We only want the popup, not the toast
      const isReminderType = payload?.type === 'reminder' || payload?.type === 'task_reminder' || payload?.type === 'REMINDER_DUE';
      const isReminderText = payload?.text?.includes('תזכורת') || payload?.text?.startsWith('תזכורת:') || payload?.text?.includes('תזכורת למשימה');
      const hasReminderId = payload?.reminderId || payload?.refId || payload?.meta?.reminderId;
      
      if (isReminderType || isReminderText || hasReminderId) {
        // Skip toast for reminders - let GlobalReminderPopup handle it
        // Dispatch window event for issue notifications
        if (payload?.type === 'new_issue') {
          window.dispatchEvent(new CustomEvent('issue:created', { detail: payload }));
        }
        return;
      }
      
      // CRITICAL FIX: Deduplicate toasts using toastId to prevent infinite loops
      const toastId = payload?.id || payload?._id || `notification-${Date.now()}-${Math.random()}`;
      
      // Check if this toast was already shown (prevent duplicates)
      if (shownToastIdsRef.current.has(toastId)) {
        return; // Already shown, skip
      }
      
      // Mark as shown
      shownToastIdsRef.current.add(toastId);
      
      // Limit the set size to prevent memory leaks (keep last 100)
      if (shownToastIdsRef.current.size > 100) {
        const firstId = Array.from(shownToastIdsRef.current)[0];
        shownToastIdsRef.current.delete(firstId);
      }
      
      const text = payload?.text || 'התראה חדשה';
      toast(text, { toastId, autoClose: 12000 });
      
      // Dispatch window event for issue notifications
      if (payload?.type === 'new_issue') {
        window.dispatchEvent(new CustomEvent('issue:created', { detail: payload }));
      }
    });

    // Handle issue events
    const unsubIssueCreated = socketManager.subscribe('issue:created', (payload: any) => {
      window.dispatchEvent(new CustomEvent('issue:created', { detail: payload }));
    });

    const unsubIssueUpdated = socketManager.subscribe('issue:updated', (payload: any) => {
      window.dispatchEvent(new CustomEvent('issue:updated', { detail: payload }));
    });

    const unsubIssueStatus = socketManager.subscribe('issue:status-changed', (payload: any) => {
      window.dispatchEvent(new CustomEvent('issue:status-changed', { detail: payload }));
    });

    // Handle announcement events
    const unsubAnnouncementNew = socketManager.subscribe('announcement:new', (payload: any) => {
      window.dispatchEvent(new CustomEvent('announcement:new', { detail: payload }));
    });

    const unsubAnnouncementUpdated = socketManager.subscribe('announcement:updated', (payload: any) => {
      window.dispatchEvent(new CustomEvent('announcement:updated', { detail: payload }));
    });

    const unsubAnnouncementDeleted = socketManager.subscribe('announcement:deleted', (payload: any) => {
      window.dispatchEvent(new CustomEvent('announcement:deleted', { detail: payload }));
    });

    // Handle user blocked event
    const unsubUserBlocked = socketManager.subscribe('user:blocked', (payload: any) => {
      const currentUserId = String(client?._id || '');
      const blockedUserId = String(payload?.userId || '');
      
      if (currentUserId && payload?.isBlocked === true &&
          (blockedUserId === currentUserId || !blockedUserId)) {
        window.dispatchEvent(new CustomEvent('user:blocked', {
          detail: {
            message: payload?.message || 'המשתמש נחסם. אתה תתנתק מהמערכת.',
            isBlocked: true,
            userId: currentUserId
          }
        }));
      }
    });

    // Handle company blocked event
    const unsubCompanyBlocked = socketManager.subscribe('company:blocked', (payload: any) => {
      const currentCompany = String(client?.company || '');
      const blockedCompanySlug = String(payload?.companySlug || '');
      
      if (payload?.isBlocked === true &&
          (blockedCompanySlug === currentCompany || !blockedCompanySlug)) {
        window.dispatchEvent(new CustomEvent('user:blocked', {
          detail: {
            message: payload?.message || 'החברה נחסמה. אתה תתנתק מהמערכת.',
            isBlocked: true,
            companyBlocked: true,
            companySlug: blockedCompanySlug
          }
        }));
      }
    });

    // Handle force refresh event (only for internal users: admin/manager/agent)
    const unsubForceRefresh = socketManager.subscribe('company:force_refresh', (payload: any) => {
      const currentCompany = String(client?.company || '');
      const currentRole = client?.role;
      
      // Only show to internal users
      const isInternal = currentRole && ['admin', 'manager', 'agent'].includes(currentRole);
      if (!isInternal) return;

      // Note: Server already filtered by company room, so if we received this event,
      // it's for our company. We still pass companyId for modal logging purposes.
      
      // Dispatch window event for ForceRefreshModal to handle
      window.dispatchEvent(new CustomEvent('company:force_refresh', {
        detail: {
          companyId: payload?.companyId || currentCompany,
          reason: payload?.reason || 'general_changed',
          changedBy: payload?.changedBy || 'superadmin',
          ts: payload?.ts || Date.now(),
        }
      }));
    });

    return () => {
      unsubInvalidate();
      unsubNotification();
      unsubIssueCreated();
      unsubIssueUpdated();
      unsubIssueStatus();
      unsubAnnouncementNew();
      unsubAnnouncementUpdated();
      unsubAnnouncementDeleted();
      unsubUserBlocked();
      unsubCompanyBlocked();
      unsubForceRefresh();
    };
  }, [isConnected, client?._id, client?.company, client?.role, queryClient]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<SocketContextValue>(() => ({
    isConnected,
    emit: socketManager.emit.bind(socketManager),
    subscribe: socketManager.subscribe.bind(socketManager),
  }), [isConnected]);

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access the socket context
 * Must be used within a SocketProvider
 */
export function useSocket(): SocketContextValue {
  const context = useContext(SocketContext);
  
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  
  return context;
}

/**
 * Hook to check if socket is available (for optional usage)
 * Returns null if not within provider
 */
export function useSocketOptional(): SocketContextValue | null {
  return useContext(SocketContext);
}

export default SocketProvider;